/* eslint-disable react-hooks/exhaustive-deps */
import Link from 'next/link'
import { Button, Form } from 'antd'
import { useTranslation } from 'next-i18next'
import React, { useState, useEffect } from 'react'
import { CustomModal, CustomText } from 'src/components'
import { CreateIntegrationPayload } from 'src/core/models'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import styles from './DevelopersAPIButtonsContent.module.scss'
import { removeAccents, showNotification } from 'src/core/helpers'
import DevelopersIntegrationForm from '../DevelopersIntegrationForm/DevelopersIntegrationForm'
import CreateIntegrationErrorsContent from './CreateIntegrationErrorsContent/CreateIntegrationErrorsContent'
import {
  createIntegration, resetCreateIntegrationState,
  hasErrorsCreateIntegration, reactivateIntegration,
  resetReactivateIntegrationState, selectCurrencyToken,
  createIntegrationSelector, reactivateIntegrationSelector
} from 'src/features'

const DevelopersAPIButtonsContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isErrorsVisible, setIsErrorsVisible] = useState(false)
  const [isFormVisible, setIsFormVisible] = useState(true)
  const currency_code = useAppSelector(selectCurrencyToken)
  const hasErrors = useAppSelector(hasErrorsCreateIntegration)
  const { success, error: createIntegrationErrors } = useAppSelector(createIntegrationSelector)
  const {
    loading: loadingReactivation,
    success: successReactivation
  } = useAppSelector(reactivateIntegrationSelector)

  const onFinish = ({ integration_code, type }: CreateIntegrationPayload) => {
    const params = {
      integration_code: removeAccents(integration_code),
      initials: removeAccents(integration_code),
      user_purchases_email: '',
      type: type
    }
    dispatch(createIntegration(params, currency_code))
  }

  useEffect(() => {
    if (success) { finishSubmit() }
    if (successReactivation) { finishSubmit() }
  }, [success, successReactivation])

  useEffect(() => {
    setIsFormVisible(!hasErrors)
    setIsErrorsVisible(hasErrors)
  }, [hasErrors])

  const handleCorrectErrors = () => {
    setIsFormVisible(true)
    setIsErrorsVisible(false)
    dispatch(resetCreateIntegrationState())
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setIsErrorsVisible(false)
    dispatch(resetCreateIntegrationState())
    form.resetFields()
  }

  const handleReactivate = () => {
    const integration_code = form.getFieldValue('integration_code')
    dispatch(reactivateIntegration(removeAccents(integration_code), currency_code))
  }

  const finishSubmit = () => {
    setIsModalVisible(false)
    showNotification('success', t('general.OperationSuccess'))
    form.resetFields()
    dispatch(resetCreateIntegrationState())
    dispatch(resetReactivateIntegrationState())
  }

  return <div className={styles.content}>
    <div className={styles.buttons}>
      {/* API Button */}
      <Link href={process.env.DEV_PORTAL_URL} passHref legacyBehavior>
        <a target='_blank' rel='noopener noreferrer'>
          <Button
            type='primary'
            size='large'
            shape='round'
            className='radius-100'
          >
            {t('developers.GoToApiDocs')}
          </Button>
        </a>
      </Link>
      {/* Create Integration Button */}
      <Button
        type='default'
        size='large'
        onClick={() => setIsModalVisible(true)}
        className={styles.createButton}
      >
        <CustomText
          label={t('developers.CreateNewIntegration')}
          className='text-color-primary'
        />
      </Button>
    </div>

    <CustomModal
      footer={null}
      open={isModalVisible}
      onCancel={handleCloseModal}
      title={t('developers.CreateNewIntegration')}
    >
      {isFormVisible && (
        <Form onFinish={onFinish} form={form}>
          <DevelopersIntegrationForm />
        </Form>
      )}
      {isErrorsVisible && (
        <CreateIntegrationErrorsContent
          errors={createIntegrationErrors}
          handleCorrectErrors={handleCorrectErrors}
          handleReactivate={handleReactivate}
          loading={loadingReactivation}
        />
      )}
    </CustomModal>
  </div>
}

export default DevelopersAPIButtonsContent
